<template>
  <ListSelect
    ref="listSelect"
    class="w-[40ch] pb-1"
    placeholder="Select preset to apply to this layer"
    :options="Object.keys(PRESETS)"
    :modelValue="''"
    @update:modelValue="applyPreset"
  />
</template>

<script setup lang="ts">
import { KeyCode, KEYCODES } from "@/keyboard";
import { useKeyboardStore, useHighlightStore } from "@/stores";
import ListSelect from "@/components/utils/ListSelect.vue";

const kb = useKeyboardStore();
const hl = useHighlightStore();

type Key =
  | KeyCode
  | null
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F"
  | "G"
  | "H"
  | "I"
  | "J"
  | "K"
  | "L"
  | "M"
  | "N"
  | "O"
  | "P"
  | "Q"
  | "R"
  | "S"
  | "T"
  | "U"
  | "V"
  | "W"
  | "X"
  | "Y"
  | "Z"
  | ";"
  | ","
  | "."
  | "/"
  | "'"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "0";

const nil = null;

const KEY_TRANSLATION: Partial<Record<Exclude<Key, null>, KeyCode>> = {
  ";": "SColon",
  ",": "Comma",
  ".": "Dot",
  "/": "Slash",
  "'": "Quote",
  "0": "Kb0",
  "1": "Kb1",
  "2": "Kb2",
  "3": "Kb3",
  "4": "Kb4",
  "5": "Kb5",
  "6": "Kb6",
  "7": "Kb7",
  "8": "Kb8",
  "9": "Kb9",
};

const key2KeyCode = (key: Key): KeyCode | undefined => {
  if (key == nil) return;

  const special = KEY_TRANSLATION[key];
  if (special != undefined) return special;

  const kc = KEYCODES.find((kc) => kc == key);
  if (kc == undefined) throw Error(`Unexpected key: $key`);

  return kc;
};

// 4x12 arrays of keys
type Preset = Key[][];
const N_ROWS = 4;
const N_COLS = 12;

const PRESETS: Record<string, Preset> = {
  Numbers: [
    [nil, "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", nil],
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
  ],
  "Numpad left": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, "7", "8", "9", nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, "4", "5", "6", nil, nil, nil, nil, nil, nil, nil],
    [nil, "0", "1", "2", "3", nil, nil, nil, nil, nil, nil, nil],
  ],
  "Numpad right": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, nil, nil, nil, nil, nil, "7", "8", "9", nil, nil],
    [nil, nil, nil, nil, nil, nil, nil, "4", "5", "6", nil, nil],
    [nil, nil, nil, nil, nil, nil, "0", "1", "2", "3", nil, nil],
  ],
  "Fn keys": [
    ["F12", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11"],
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
  ],
  "QWERTY (1870)": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, "Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P", nil],
    [nil, "A", "S", "D", "F", "G", "H", "J", "K", "L", ";", nil],
    [nil, "Z", "X", "C", "V", "B", "N", "M", ",", ".", "/", nil],
  ],
  "Dworak (1936)": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, "'", ",", ".", "P", "Y", "F", "G", "C", "R", "L", nil],
    [nil, "A", "O", "E", "U", "I", "D", "H", "T", "N", "S", nil],
    [nil, ";", "Q", "J", "K", "X", "B", "M", "W", "V", "Z", nil],
  ],
  "Colemak (2005)": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, "Q", "W", "F", "P", "G", "J", "L", "U", "Y", ";", nil],
    [nil, "A", "R", "S", "T", "D", "H", "N", "E", "I", "O", nil],
    [nil, "Z", "X", "C", "V", "B", "K", "M", ",", ".", "/", nil],
  ],
  "Workman (2010)": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, "Q", "D", "R", "W", "B", "J", "F", "U", "P", ";", nil],
    [nil, "A", "S", "H", "T", "G", "Y", "N", "E", "O", "I", nil],
    [nil, "Z", "X", "M", "C", "V", "K", "L", ",", ".", "/", nil],
  ],
  "Halmak (2016)": [
    [nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil, nil],
    [nil, "W", "L", "R", "B", "Z", ";", "Q", "U", "D", "J", nil],
    [nil, "S", "H", "N", "T", ",", ".", "A", "E", "O", "I", nil],
    [nil, "F", "M", "V", "C", "/", "G", "P", "X", "K", "Y", nil],
  ],
};

const applyPreset = (name: string) => {
  const preset = PRESETS[name];
  if (name == undefined) return;

  const positions: [number, number][] = [];

  for (let row = 0; row < N_ROWS; row++) {
    for (let col = 0; col < N_COLS; col++) {
      const kc = key2KeyCode(preset[row][col]);
      if (kc == nil) continue;
      kb.setAction(row, col, { KeyCode: kc });
      positions.push([row, col]);
    }
  }

  hl.setHighlightedKeys(positions, 300);
};
</script>
